<script setup>
import { computed } from "vue";
import { useRouter } from "vue-router";
import Button from "primevue/button";
import { usePermissions } from "@/shared/composables/usePermissions";

const router = useRouter();
const { can, loaded } = usePermissions();

function navigateTo(path) {
  router.push(path);
}

const canEditProducts = computed(() => can("EditProducts"));
const canImportProductImages = computed(() => can("ImportProductImages"));
const canEditProductAttributes = computed(() => can("EditProductAttributes"));
const canEditProductTypes = computed(() => can("EditProductTypes"));
const canViewUsers = computed(() => can("ViewUsers"));
const canReviewRegistrations = computed(() => can("ReviewRegistrations"));
const canManageRoles = computed(() => can("ManageRoles"));
const canEditBrands = computed(() => can("EditBrand"));
const canEditCategories = computed(() => can("EditCategory"));
const canViewDiscount = computed(() => can("ViewDiscount"));
const canEditCarousel = computed(() => can("EditCarousel"));
const canEditWeeklySelection = computed(() => can("EditWeeklySelection"));
const canChangeOrderStatus = computed(() => can("ChangeOrderStatus"));
const canViewManagersStats = computed(() => can("ViewManagersStats"));
</script>

<template>
  <div class="admin-page">
    <div class="page-shell">
      <header class="page-header">
        <div>
          <h1>Панель администратора</h1>
          <p>Управление системой, каталогом и доступами</p>
        </div>
      </header>

      <div v-if="!loaded" class="loading-state">
        <div class="spinner"></div>
        <p>Загрузка прав доступа...</p>
      </div>

      <div v-else class="admin-grid">
        <section v-if="canEditCarousel || canEditWeeklySelection" class="admin-card">
          <div class="card-header">
            <div>
              <h2>Главная страница</h2>
              <p>Управление контентом главной страницы</p>
            </div>
            <span class="card-dot"></span>
          </div>
          <div class="card-actions">
            <Button
              v-if="canEditCarousel"
              label="Карусель"
              icon="pi pi-images"
              class="admin-btn"
              outlined
              @click="navigateTo('/admin/homepage/carousel')"
            >
              <template #subtitle>
                <span class="btn-subtitle">Слайды на главной странице</span>
              </template>
            </Button>
            <Button
              v-if="canEditWeeklySelection"
              label="Еженедельный отбор"
              icon="pi pi-star"
              class="admin-btn"
              outlined
              @click="navigateTo('/admin/homepage/weekly-selection')"
            >
              <template #subtitle>
                <span class="btn-subtitle">Товары недели</span>
              </template>
            </Button>
          </div>
        </section>

        <section
          v-if="
            canEditProducts || canImportProductImages || canEditProductAttributes || canEditProductTypes
          "
          class="admin-card"
        >
          <div class="card-header">
            <div>
              <h2>Товары</h2>
              <p>Управление товарами и их структурой</p>
            </div>
            <span class="card-dot"></span>
          </div>
          <div class="card-actions">
            <Button
              v-if="canEditProducts"
              label="Все товары"
              icon="pi pi-box"
              class="admin-btn"
              outlined
              @click="navigateTo('/admin/products')"
            />
            <Button
              v-if="canEditProducts"
              label="Импорт каталогов"
              icon="pi pi-file-import"
              class="admin-btn"
              outlined
              @click="navigateTo('/admin/imports')"
            />
            <Button
              v-if="canImportProductImages"
              label="Импорт изображений"
              icon="pi pi-images"
              class="admin-btn"
              outlined
              @click="navigateTo('/admin/product-image-imports')"
            />
            <Button
              v-if="canEditProductAttributes"
              label="Атрибуты"
              icon="pi pi-tags"
              class="admin-btn"
              outlined
              @click="navigateTo('/admin/attributes')"
            />
            <Button
              v-if="canEditProductTypes"
              label="Основные товары"
              icon="pi pi-list"
              class="admin-btn"
              outlined
              @click="navigateTo('/admin/product-types')"
            />
          </div>
        </section>

        <section v-if="canViewUsers || canManageRoles || canReviewRegistrations" class="admin-card">
          <div class="card-header">
            <div>
              <h2>Пользователи</h2>
              <p>Управление пользователями и ролями</p>
            </div>
            <span class="card-dot"></span>
          </div>
          <div class="card-actions">
            <Button
              v-if="canViewUsers"
              label="Управление пользователями"
              icon="pi pi-users"
              class="admin-btn"
              outlined
              @click="navigateTo('/admin/users')"
            />
            <Button v-if="canReviewRegistrations" label="Заявки на регистрацию" icon="pi pi-user-plus"
              class="admin-btn" outlined @click="navigateTo('/admin/registrations')" />
            <Button
              v-if="canManageRoles"
              label="Роли"
              icon="pi pi-id-card"
              class="admin-btn"
              outlined
              @click="navigateTo('/admin/roles')"
            />
          </div>
        </section>

        <section v-if="canEditBrands || canEditCategories" class="admin-card">
          <div class="card-header">
            <div>
              <h2>Каталог</h2>
              <p>Управление брендами и категориями</p>
            </div>
            <span class="card-dot"></span>
          </div>
          <div class="card-actions">
            <Button
              v-if="canEditBrands"
              label="Бренды"
              icon="pi pi-tags"
              class="admin-btn"
              outlined
              @click="navigateTo('/admin/brands')"
            />
            <Button
              v-if="canEditCategories"
              label="Категории"
              icon="pi pi-sitemap"
              class="admin-btn"
              outlined
              @click="navigateTo('/admin/categories')"
            />
          </div>
        </section>

        <section v-if="canViewDiscount" class="admin-card">
          <div class="card-header">
            <div>
              <h2>Скидки</h2>
              <p>Настройка скидок для пользователей и брендов</p>
            </div>
            <span class="card-dot"></span>
          </div>
          <div class="card-actions">
            <Button
              label="Управление скидками"
              icon="pi pi-percentage"
              class="admin-btn"
              outlined
              @click="navigateTo('/admin/discounts')"
            />
          </div>
        </section>

        <section v-if="canChangeOrderStatus" class="admin-card">
          <div class="card-header">
            <div>
              <h2>Заказы</h2>
              <p>Просмотр и управление заказами</p>
            </div>
            <span class="card-dot"></span>
          </div>
          <div class="card-actions">
            <Button
              label="История заказов"
              icon="pi pi-shopping-cart"
              class="admin-btn"
              outlined
              @click="navigateTo('/admin/orders')"
            />
          </div>
        </section>

        <section v-if="canViewManagersStats" class="admin-card">
          <div class="card-header">
            <div>
              <h2>Статистика менеджеров</h2>
              <p>Рейтинг и эффективность работы</p>
            </div>
            <span class="card-dot"></span>
          </div>
          <div class="card-actions">
            <Button
              label="Топ менеджеров"
              icon="pi pi-chart-line"
              class="admin-btn"
              outlined
              @click="navigateTo('/admin/top-managers')"
            />
          </div>
        </section>
      </div>
    </div>
  </div>
</template>

<style scoped>
.admin-page {
  min-height: 100vh;
  background: #fafafa;
}

.page-shell {
  max-width: 1240px;
  margin: 0 auto;
  padding: 32px 24px 48px;
}

.page-header {
  margin-bottom: 28px;
}

.page-header h1 {
  margin: 0;
  font-size: 36px;
  line-height: 1.1;
  letter-spacing: -0.03em;
  color: #111111;
}

.page-header p {
  margin: 10px 0 0;
  font-size: 15px;
  color: #6b6b6b;
}

.admin-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 18px;
}

.admin-card {
  display: flex;
  flex-direction: column;
  gap: 18px;
  padding: 22px;
  border: 1px solid #ececec;
  border-radius: 24px;
  background: #ffffff;
  box-shadow: 0 18px 40px rgba(17, 17, 17, 0.05);
}

.card-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
}

.card-header h2 {
  margin: 0;
  font-size: 22px;
  color: #111111;
}

.card-header p {
  margin: 8px 0 0;
  font-size: 14px;
  color: #6b6b6b;
}

.card-dot {
  width: 10px;
  height: 10px;
  margin-top: 8px;
  border-radius: 999px;
  background: #c41217;
  flex-shrink: 0;
}

.card-actions {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

:deep(.admin-btn.p-button) {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: flex-start;
  gap: 4px;
  width: 100%;
  border-radius: 14px;
  border: 1px solid #dddddd;
  background: #ffffff;
  color: #111111;
  padding: 12px 14px;
}

:deep(.admin-btn.p-button:hover) {
  background: #f7f7f7;
  border-color: #111111;
}

:deep(.admin-btn.p-button .p-button-icon) {
  color: #777777;
  transition: color 0.2s ease;
  margin-bottom: 6px;
}

:deep(.admin-btn.p-button:hover .p-button-icon) {
  color: #c41217;
}

:deep(.admin-btn.p-button .p-button-label) {
  font-weight: 500;
  font-size: 15px;
}

.btn-subtitle {
  font-size: 12px;
  color: #888888;
  font-weight: normal;
  transition: color 0.2s ease;
}

:deep(.admin-btn.p-button:hover .btn-subtitle) {
  color: #555555;
}

.loading-state {
  text-align: center;
  padding: 60px 20px;
  background: white;
  border-radius: 20px;
}

.spinner {
  width: 40px;
  height: 40px;
  border: 3px solid #f3f3f3;
  border-top: 3px solid #c41217;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 20px;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

@media (max-width: 1024px) {
  .admin-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 640px) {
  .page-shell {
    padding: 20px 16px 36px;
  }

  .page-header h1 {
    font-size: 26px;
  }

  .page-header p {
    font-size: 14px;
  }

  .admin-grid {
    grid-template-columns: 1fr;
    gap: 14px;
  }

  .admin-card {
    padding: 18px 16px;
    border-radius: 18px;
    gap: 14px;
  }

  .card-header h2 {
    font-size: 19px;
  }
}

@media (max-width: 400px) {
  .page-header h1 {
    font-size: 22px;
  }

  :deep(.admin-btn.p-button) {
    padding: 10px 12px;
    font-size: 13px;
  }
}
</style>
